import axios from 'axios';

import { getRequestConfig } from '../auth/auth';
import { ApiGateway } from '../config/config';

// Some target APIs return { results, instance } while others return { data }.
// Normalize payloads here so UI callers can safely keep using results/instance.
function normalizeResultsPayload(payload) {
  const safePayload = payload ?? {};
  const results = Array.isArray(safePayload.results)
    ? safePayload.results
    : (Array.isArray(safePayload.data) ? safePayload.data : []);

  return {
    ...safePayload,
    results
  };
}

function normalizeInstancePayload(payload) {
  const safePayload = payload ?? {};
  const instance = safePayload.instance ?? (
    safePayload.data && !Array.isArray(safePayload.data)
      ? safePayload.data
      : undefined
  );

  return {
    ...safePayload,
    instance
  };
}

// Helper: strip out object/array props from update body
function stripNestedProps(obj) {
  const body = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== null && typeof value !== 'object') {
      body[key] = value;
    }
  }
  return body;
}

// --- TargetFacet CRUD ---
export async function getTargetFacets(search) {
  const config = await getRequestConfig();
  const body = search ?? {}
  const resp = await axios.post(`${ApiGateway.target}/facet/search`, body, config);
  return normalizeResultsPayload(resp.data);
}

export async function addTargetFacet(facet) {
  const config = await getRequestConfig();
  const resp = await axios.put(`${ApiGateway.target}/facet`, facet, config);
  return normalizeInstancePayload(resp.data);
}

export async function updateTargetFacet(facet) {
  if (!facet.id) return { status: 'error', error: 'ID is required for update' };
  const config = await getRequestConfig();
  const body = stripNestedProps(facet); // remove targetMetrics or any nested
  const resp = await axios.put(`${ApiGateway.target}/facet/${facet.id}`, body, config);
  return normalizeInstancePayload(resp.data);
}

export async function deleteTargetFacet(id) {
  const config = await getRequestConfig();
  const resp = await axios.delete(`${ApiGateway.target}/facet/${id}`, config);
  return resp.data;
}

// --- TargetMetric CRUD ---
export async function getTargetMetrics() {
  const config = await getRequestConfig();
  const resp = await axios.post(`${ApiGateway.target}/metric/search`, {}, config);
  return normalizeResultsPayload(resp.data);
}

export async function addTargetMetric(metric) {
  const config = await getRequestConfig();
  const resp = await axios.put(`${ApiGateway.target}/metric`, metric, config);
  return normalizeInstancePayload(resp.data);
}

export async function updateTargetMetric(metric) {
  if (!metric.id) return { status: 'error', error: 'ID is required for update' };
  const config = await getRequestConfig();
  const body = stripNestedProps(metric);
  const resp = await axios.put(`${ApiGateway.target}/metric/${metric.id}`, body, config);
  return normalizeInstancePayload(resp.data);
}

export async function deleteTargetMetric(metricId) {
  const config = await getRequestConfig();
  const resp = await axios.delete(`${ApiGateway.target}/metric/${metricId}`, config);
  return resp.data;
}

// --- TargetPeriod CRUD ---
export async function getTargetPeriods() {
  const config = await getRequestConfig();
  const resp = await axios.post(`${ApiGateway.target}/period/search`, {}, config);
  return normalizeResultsPayload(resp.data);
}

export async function addTargetPeriod(period) {
  const config = await getRequestConfig();
  const resp = await axios.put(`${ApiGateway.target}/period`, period, config);
  return normalizeInstancePayload(resp.data);
}

export async function updateTargetPeriod(period) {
  if (!period.id) return { status: 'error', error: 'ID is required for update' };
  const config = await getRequestConfig();
  const body = stripNestedProps(period);
  const resp = await axios.put(`${ApiGateway.target}/period/${period.id}`, body, config);
  return normalizeInstancePayload(resp.data);
}

export async function deleteTargetPeriod(id) {
  const config = await getRequestConfig();
  const resp = await axios.delete(`${ApiGateway.target}/period/${id}`, config);
  return resp.data;
}

export async function getOwnershipTargetMetrics(targetPeriodId, targetFacetId, ownershipId) {
  const config = await getRequestConfig();

  const promises = [
    axios.post(`${ApiGateway.target}/metric/search`, { targetFacetId, active: true }, config),
    axios.post(`${ApiGateway.target}/value/search`, { targetPeriodId, ownershipId }, config),
    axios.post(`${ApiGateway.target}/periodAck/search`, { targetPeriodId, targetFacetId, ownershipId }, config)
  ]
  const otm = {
    targetMetrics: undefined,
    targetMetricValues: undefined,
    targetPeriodAck: undefined,
  }
  const responses = await Promise.all(promises)
  const metricsPayload = normalizeResultsPayload(responses[0].data)
  const valuesPayload = normalizeResultsPayload(responses[1].data)
  const acksPayload = normalizeResultsPayload(responses[2].data)
  const metrics = metricsPayload.results
  const values = valuesPayload.results
  const acks = acksPayload.results

  otm.targetPeriodAck = (acks?.length === 1) ? acks[0] : acks.find(a => a.targetFacetId === targetFacetId)
  otm.targetMetrics = metrics
  otm.targetMetricValues = metrics.map(m => {
    const val = values.find((v) => v.targetMetricId === m.id)
    const value = val && val.value != null ? parseFloat(val.value) : null
    return {
      targetPeriodId: targetPeriodId,
      targetMetricId: m.id,
      ownershipId: ownershipId,
      description: m.description,
      value
    }
  })

  return otm;
}

export async function saveTargetMetricValues(metrics) {
  const config = await getRequestConfig()
  
  await axios.put(`${ApiGateway.target}/value`, metrics, config)
}

export async function addAck(targetPeriodId, targetFacetId, ownershipId, isLead = false) {
  const config = await getRequestConfig()
  
  const response = await axios.put(`${ApiGateway.target}/periodAck`, {targetPeriodId, targetFacetId, ownershipId, isLead}, config)
  return response.data
}

export async function removeAck(targetPeriodId, targetFacetId, ownershipId, isLead = false) {
  const config = await getRequestConfig()
  
  const response = await axios.put(`${ApiGateway.target}/periodAck/remove`, {targetPeriodId, targetFacetId, ownershipId, isLead}, config)
  return response.data
}